import { faUser } from '@fortawesome/free-solid-svg-icons';
import { faHammerBrush } from '@fortawesome/pro-duotone-svg-icons';
import {
  faBuildings,
  faUsers,
  faLink,
  faCreditCard,
  faRightFromBracket,
  faHome,
  faGear,
  faUser as faUserRegular,
  faMessagePlus,
  faCircleQuestion,
  faStore,
  faBell,
  faBriefcase,
} from '@fortawesome/pro-regular-svg-icons';
import {
  SidebarNavGroup,
  SidebarNavItem,
} from './sidebar-navigation.models';
import { BILLING_ENABLED } from '@core/config/billing.config';

export type SettingsSidebarSection =
  | 'profile'
  | 'general'
  | 'company'
  | 'users'
  | 'connections'
  | 'payments'
  | 'agents'
  | 'alerts';

export type SettingsSidebarAction =
  | 'profile'
  | 'general'
  | 'company'
  | 'users'
  | 'connections'
  | 'payments'
  | 'agents'
  | 'alerts';

export type ProfileSidebarSection =
  | 'personal'
  | 'profile'
  | 'company'
  | 'users'
  | 'payments';

export type ProfileSidebarAction =
  | 'personal'
  | 'profile'
  | 'company'
  | 'users'
  | 'payments'
  | 'logout';

export type BackofficeSidebarSection = 'home' | 'profile' | 'payments';

export type BackofficeSidebarAction =
  | 'home'
  | 'profile'
  | 'payments'
  | 'settings';

export type DashboardSidebarSection =
  | 'home'
  | 'agents'
  | 'connections'
  | 'knowledge'
  | 'alerts'
  | 'clients'
  | 'help-center';

export type DashboardSidebarAction =
  | 'home'
  | 'new-chat'
  | 'agents'
  | 'knowledge'
  | 'alerts'
  | 'clients'
  | 'help-center'
  | 'settings'
  | 'connections';

export const SETTINGS_PRIMARY_GROUP: SidebarNavGroup<
  SettingsSidebarAction,
  SettingsSidebarSection
> = {
  id: 'settings-primary',
  items: [
    {
      id: 'settings-profile',
      label: 'Mi perfil',
      icon: faUser,
      action: 'profile',
      activeKey: 'profile',
    },
    {
      id: 'settings-general',
      label: 'General',
      icon: faHammerBrush,
      action: 'general',
      activeKey: 'general',
    },
  ],
};

export const SETTINGS_ADMIN_GROUP: SidebarNavGroup<
  SettingsSidebarAction,
  SettingsSidebarSection
> = {
  id: 'settings-admin',
  separatorBefore: true,
  title: 'ÁREA DE ADMINISTRADOR',
  items: [
    {
      id: 'settings-company',
      label: 'Mi empresa',
      icon: faBuildings,
      action: 'company',
      activeKey: 'company',
    },
    {
      id: 'settings-users',
      label: 'Usuarios',
      icon: faUsers,
      action: 'users',
      activeKey: 'users',
    },
  ],
};

export const SETTINGS_OPERATIONS_GROUP: SidebarNavGroup<
  SettingsSidebarAction,
  SettingsSidebarSection
> = {
  id: 'settings-operations',
  items: [
    {
      id: 'settings-connections',
      label: 'Conexiones',
      icon: faLink,
      action: 'connections',
      activeKey: 'connections',
    },
    // billing.config — puerta 1 de 4 a facturación. Spread condicional en vez
    // de comentar el objeto, para que vuelva sola al poner el flag en true.
    ...(BILLING_ENABLED
      ? [
          {
            id: 'settings-payments',
            label: 'Facturación',
            icon: faCreditCard,
            action: 'payments' as const,
            activeKey: 'payments' as const,
          },
        ]
      : []),
  ],
};

export const SETTINGS_AGENTS_GROUP: SidebarNavGroup<
  SettingsSidebarAction,
  SettingsSidebarSection
> = {
  id: 'settings-agents',
  separatorBefore: true,
  items: [
    {
      id: 'settings-agents-link',
      label: 'Mis agentes',
      icon: faUsers,
      action: 'agents',
      activeKey: 'agents',
    },
    {
      id: 'settings-alerts-link',
      label: 'Mis alertas',
      icon: faBell,
      action: 'alerts',
      activeKey: 'alerts',
    },
  ],
};

export const PROFILE_ADMIN_GROUP: SidebarNavGroup<
  ProfileSidebarAction,
  ProfileSidebarSection
> = {
  id: 'profile-admin',
  title: 'Área de administrador',
  items: [
    {
      id: 'profile-main',
      label: 'Mi perfil',
      icon: faUser,
      action: 'profile',
      activeKey: 'profile',
    },
    {
      id: 'profile-company',
      label: 'Mi empresa',
      icon: faBuildings,
      action: 'company',
      activeKey: 'company',
    },
    {
      id: 'profile-users',
      label: 'Gestión de usuarios',
      icon: faUsers,
      action: 'users',
      activeKey: 'users',
    },
    // billing.config — puerta 2 de 4. Misma pantalla que la de Configuración,
    // pero por su propia ruta /dashboard/payments-invoices.
    ...(BILLING_ENABLED
      ? [
          {
            id: 'profile-payments',
            label: 'Pagos y facturas',
            icon: faCreditCard,
            action: 'payments' as const,
            activeKey: 'payments' as const,
          },
        ]
      : []),
  ],
};

export const PROFILE_USER_GROUP: SidebarNavGroup<
  ProfileSidebarAction,
  ProfileSidebarSection
> = {
  id: 'profile-user',
  title: 'Área de usuario',
  items: [
    {
      id: 'profile-personal',
      label: 'Mis datos personales',
      icon: faUser,
      action: 'personal',
      activeKey: 'personal',
    },
    {
      id: 'profile-logout',
      label: 'Cerrar sesión',
      icon: faRightFromBracket,
      action: 'logout',
      title: 'Cerrar sesión',
    },
  ],
};

export const BACKOFFICE_PRIMARY_NAV_ITEMS: SidebarNavItem<
  BackofficeSidebarAction,
  BackofficeSidebarSection
>[] = [
    {
      id: 'backoffice-home',
      label: 'Inicio',
      icon: faHome,
      action: 'home',
      activeKey: 'home',
      title: 'Inicio',
    },
    // billing.config — puerta 3 de 4. Apunta a un componente DISTINTO y más
    // antiguo (payments-invoices-backoffice), con su propio Stripe Elements.
    ...(BILLING_ENABLED
      ? [
          {
            id: 'backoffice-payments',
            label: 'Pagos y Facturas',
            icon: faCreditCard,
            action: 'payments' as const,
            activeKey: 'payments' as const,
            title: 'Pagos y Facturas',
          },
        ]
      : []),
  ];

export const BACKOFFICE_SECONDARY_NAV_ITEMS: SidebarNavItem<
  BackofficeSidebarAction,
  BackofficeSidebarSection
>[] = [
    {
      id: 'backoffice-profile',
      label: 'Mi perfil',
      icon: faUserRegular,
      action: 'profile',
      activeKey: 'profile',
      title: 'Mi perfil',
    },
    {
      id: 'backoffice-settings',
      label: 'Configuración',
      icon: faGear,
      action: 'settings',
      title: 'Configuración',
    },
  ];

export const BACKOFFICE_PRIMARY_GROUP: SidebarNavGroup<
  BackofficeSidebarAction,
  BackofficeSidebarSection
> = {
  id: 'backoffice-primary',
  items: BACKOFFICE_PRIMARY_NAV_ITEMS,
};

export const BACKOFFICE_SECONDARY_GROUP: SidebarNavGroup<
  BackofficeSidebarAction,
  BackofficeSidebarSection
> = {
  id: 'backoffice-secondary',
  items: BACKOFFICE_SECONDARY_NAV_ITEMS,
};

export const DASHBOARD_PRIMARY_GROUP: SidebarNavGroup<
  DashboardSidebarAction,
  DashboardSidebarSection
> = {
  id: 'dashboard-primary',
  items: [
    {
      id: 'dashboard-home',
      label: 'Inicio',
      icon: faHome,
      action: 'home',
      activeKey: 'home',
      title: 'Inicio',
    },
    {
      id: 'dashboard-new-chat',
      label: 'Nuevo chat',
      icon: faMessagePlus,
      action: 'new-chat',
      title: 'Nuevo chat',
    },
    {
      id: 'dashboard-agents',
      label: 'Agentes',
      icon: faUsers,
      action: 'agents',
      activeKey: 'agents',
      title: 'Agentes',
    },
    {
      id: 'dashboard-knowledge',
      label: 'Mi empresa',
      icon: faBuildings,
      action: 'knowledge',
      activeKey: 'knowledge',
      title: 'Mi empresa',
    },
    {
      id: 'dashboard-clients',
      label: 'Clientes',
      icon: faBriefcase,
      action: 'clients',
      activeKey: 'clients',
      title: 'Clientes',
    },
    {
      id: 'dashboard-alerts',
      label: 'Alertas',
      icon: faBell,
      action: 'alerts',
      activeKey: 'alerts',
      title: 'Alertas',
    },
  ],
};

export const DASHBOARD_FOOTER_GROUP: SidebarNavGroup<
  DashboardSidebarAction,
  DashboardSidebarSection
> = {
  id: 'dashboard-footer',
  separatorBefore: true,
  items: [
    {
      id: 'dashboard-help-center',
      label: 'Centro de Ayuda',
      icon: faCircleQuestion,
      action: 'help-center',
      activeKey: 'help-center',
      title: 'Centro de Ayuda',
    },
    {
      id: 'dashboard-settings',
      label: 'Configuración',
      icon: faGear,
      action: 'settings',
      title: 'Configuración',
    },
    {
      id: 'dashboard-connections',
      label: 'Marketplace ARDIA',
      icon: faStore,
      action: 'connections',
      activeKey: 'connections',
      title: 'Marketplace ARDIA',
    },
  ],
};
